/* Admin: Events list + editor + per-event pledges (manual donations). */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Link, navigate } from "../../lib/router-compat.jsx";
import AdminShell from "../../components/shell/AdminShell.jsx";
import {
  Button, Field, Input, Textarea, Select, Card, Badge, EmptyState, Skeleton,
  ConfirmDialog, Icon, MediaUploaderField, RichTextEditor, showToast,
} from "../../ui/index.js";
import { useEvents, useEvent, useLeads, useSiteSettings } from "../../data/hooks.js";
import { saveEvent, deleteEvent, saveDonation, markDonationReceived, updateLeadStatus } from "../../data/mutations.js";
import { formatDate, formatRelative, CURRENCY_FMT, slugify, toDatetimeLocal } from "../../utils/format.js";

/* ---------------- List ---------------- */
export function AdminEventsListPage() {
  const { events, loading } = useEvents();
  const [deleting, setDeleting] = useState(null);

  const onDelete = async () => {
    try { await deleteEvent(deleting.slug); showToast({ intent: "success", title: "Event deleted" }); }
    catch (err) { showToast({ intent: "danger", title: "Couldn't delete", body: err.message }); }
    setDeleting(null);
  };

  return (
    <AdminShell>
      <div className="admin-main__header">
        <div><h1 className="t-3xl">Events</h1><p>Create upcoming events and collect donations toward them.</p></div>
        <Link to="/admin/events/new" className="btn btn--primary"><Icon.Plus size={16} />New event</Link>
      </div>

      {loading ? <Skeleton h={240} /> : events.length === 0 ? (
        <Card><EmptyState icon={<Icon.Calendar size={40} />} title="No events yet" body="Create your first upcoming event." action={<Link to="/admin/events/new" className="btn btn--primary"><Icon.Plus size={16} />New event</Link>} /></Card>
      ) : (
        <table className="table">
          <thead><tr><th>Event</th><th style={{ width: 130 }}>Status</th><th style={{ width: 160 }}>Date</th><th style={{ width: 130 }}>Raised</th><th style={{ width: 110 }}>Visible</th><th className="table__actions">Actions</th></tr></thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.slug} className="clickable" onClick={() => navigate(`/admin/events/${e.slug}/edit`)}>
                <td><div style={{ fontWeight: 600 }}>{e.title}</div><div className="t-xs muted">{e.location}</div></td>
                <td><Badge variant={e.status === "upcoming" ? "success" : "neutral"} noDot>{e.status === "upcoming" ? "Upcoming" : "Past"}</Badge></td>
                <td className="t-sm">{formatDate(e.startAt)}</td>
                <td className="t-sm">{e.donation?.enabled ? CURRENCY_FMT(e.totals?.receivedAmount || 0, e.donation?.currency || "GHS") : "—"}</td>
                <td>{e.published ? <Badge variant="published">Live</Badge> : <Badge variant="draft">Hidden</Badge>}</td>
                <td className="table__actions" onClick={(ev) => ev.stopPropagation()}>
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/events/${e.slug}/pledges`)} aria-label="Pledges"><Icon.Users size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/events/${e.slug}/edit`)} aria-label="Edit"><Icon.Pencil size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleting(e)} aria-label="Delete"><Icon.Trash size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ConfirmDialog open={!!deleting} title={`Delete '${deleting?.title}'?`} body="This cannot be undone. Pledges and donations linked to it are kept." confirmLabel="Delete" destructive onConfirm={onDelete} onClose={() => setDeleting(null)} />
    </AdminShell>
  );
}

/* ---------------- Editor ---------------- */
const EMPTY = {
  slug: "", title: "", status: "upcoming", startAt: null, endAt: null, location: "",
  summary: "", bodyHtml: "", heroImage: null, published: false,
  donation: { enabled: false, goalAmount: "", currency: "GHS", bankName: "", bankAccountName: "", bankAccountNumber: "", bankBranch: "", swift: "", momoProvider: "MTN Mobile Money", momoNumber: "", momoName: "", notes: "" },
};

export function AdminEventEditorPage({ isNew }) {
  const { eventId } = useParams();
  const { event: remote, loading } = useEvent(isNew ? null : eventId);
  const { settings } = useSiteSettings();
  const [ev, setEv] = useState(EMPTY);
  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const seeded = useRef(false);

  useEffect(() => {
    if (isNew) { seeded.current = true; return; }
    if (!loading && remote && !seeded.current) {
      setEv({ ...EMPTY, ...remote, donation: { ...EMPTY.donation, ...(remote.donation || {}) } });
      setStartLocal(toDatetimeLocal(remote.startAt));
      setEndLocal(toDatetimeLocal(remote.endAt));
      setSlugTouched(true);
      seeded.current = true;
    }
  }, [isNew, loading, remote]);

  const set = (k, v) => setEv((s) => ({ ...s, [k]: v }));
  const setDon = (k, v) => setEv((s) => ({ ...s, donation: { ...s.donation, [k]: v } }));

  // When enabling donations and bank fields are empty, prefill from site settings.
  const enableDonations = (checked) => {
    setEv((s) => {
      const base = checked && !s.donation.bankName && settings?.donation ? { ...s.donation, ...settings.donation, enabled: true } : { ...s.donation, enabled: checked };
      return { ...s, donation: base };
    });
  };

  const persist = async () => {
    if (!ev.title.trim()) { showToast({ intent: "danger", title: "Add a title first" }); return; }
    const slug = ev.slug || slugify(ev.title);
    setSaving(true);
    try {
      await saveEvent(slug, {
        title: ev.title.trim(), status: ev.status,
        startAt: startLocal ? new Date(startLocal) : null,
        endAt: endLocal ? new Date(endLocal) : null,
        location: ev.location, summary: ev.summary, bodyHtml: ev.bodyHtml,
        heroImage: ev.heroImage, published: !!ev.published,
        donation: { ...ev.donation, goalAmount: ev.donation.goalAmount ? Number(ev.donation.goalAmount) : null },
        ...(remote ? {} : { totals: { pledgedCount: 0, receivedAmount: 0 } }),
      });
      showToast({ intent: "success", title: isNew ? "Event created" : "Event saved" });
      if (isNew) navigate(`/admin/events/${slug}/edit`, { replace: true });
    } catch (err) {
      showToast({ intent: "danger", title: "Couldn't save", body: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (!isNew && loading && !seeded.current) return <AdminShell><Skeleton h={400} /></AdminShell>;
  const d = ev.donation;

  return (
    <AdminShell>
      <Link to="/admin/events" className="back-link"><Icon.ArrowLeft size={14} />Back to events</Link>
      <div className="savebar" style={{ marginTop: 8 }}>
        <h1 className="t-lg" style={{ margin: 0, flex: 1 }}>{isNew ? "New event" : `Editing: ${ev.title}`}</h1>
        {!isNew && <Link to={`/admin/events/${eventId}/pledges`} className="btn btn--ghost"><Icon.Users size={14} />Pledges</Link>}
        {!isNew && ev.published && <a href={`/events/${ev.slug}`} target="_blank" rel="noreferrer" className="btn btn--ghost">View <Icon.ExternalLink size={14} /></a>}
        <Button variant="primary" loading={saving} onClick={persist}>{saving ? "Saving..." : "Save"}</Button>
      </div>

      <div className="dashboard-split" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 32, marginTop: 24 }}>
        <div className="stack-lg">
          <Field label="Title"><Input size="lg" value={ev.title} onChange={(e) => { const title = e.target.value; setEv((s) => ({ ...s, title, slug: slugTouched ? s.slug : slugify(title) })); }} placeholder="Event title" /></Field>
          <Field label="URL slug" helper="Used in the link: /events/your-slug"><Input value={ev.slug} onChange={(e) => { setSlugTouched(true); set("slug", slugify(e.target.value)); }} disabled={!isNew} /></Field>
          <Field label="Summary" helper="One line shown in the events list."><Textarea value={ev.summary} onChange={(e) => set("summary", e.target.value)} rows={2} /></Field>
          <Field label="Details"><RichTextEditor value={ev.bodyHtml} onChange={(v) => set("bodyHtml", v)} instanceKey={eventId || "new"} /></Field>
        </div>

        <div className="stack-lg">
          <Card>
            <h3 className="t-lg" style={{ marginTop: 0 }}>Schedule</h3>
            <Field label="Status"><Select value={ev.status} onChange={(e) => set("status", e.target.value)}><option value="upcoming">Upcoming</option><option value="past">Past</option></Select></Field>
            <Field label="Starts"><Input type="datetime-local" value={startLocal} onChange={(e) => setStartLocal(e.target.value)} /></Field>
            <Field label="Ends (optional)"><Input type="datetime-local" value={endLocal} onChange={(e) => setEndLocal(e.target.value)} /></Field>
            <Field label="Location"><Input value={ev.location} onChange={(e) => set("location", e.target.value)} placeholder="Accra, Ghana" /></Field>
          </Card>

          <Card>
            <h3 className="t-lg" style={{ marginTop: 0 }}>Hero image</h3>
            <MediaUploaderField value={ev.heroImage} onChange={(v) => set("heroImage", v)} path={`events/${ev.slug || "new"}`} aspect="16/9" />
          </Card>

          <Card>
            <label className="row" style={{ gap: 8, cursor: "pointer" }}><input type="checkbox" checked={!!ev.published} onChange={(e) => set("published", e.target.checked)} /><span className="t-sm" style={{ fontWeight: 600 }}>Published (visible on the public site)</span></label>
          </Card>
        </div>
      </div>

      <Card style={{ marginTop: 24 }}>
        <label className="row" style={{ gap: 8, cursor: "pointer" }}><input type="checkbox" checked={!!d.enabled} onChange={(e) => enableDonations(e.target.checked)} /><span className="t-2xl" style={{ fontWeight: 600 }}>Collect donations for this event</span></label>
        {d.enabled && (
          <div className="stack-lg" style={{ marginTop: 20 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Field label="Fundraising goal (optional)"><Input type="number" value={d.goalAmount} onChange={(e) => setDon("goalAmount", e.target.value)} placeholder="e.g. 50000" /></Field>
              <Field label="Currency"><Select value={d.currency} onChange={(e) => setDon("currency", e.target.value)}><option>GHS</option><option>USD</option><option>GBP</option><option>EUR</option></Select></Field>
            </div>
            <p className="t-sm muted" style={{ margin: 0 }}>Transfer details shown to donors for this event (prefilled from site settings — edit if this event uses a different account):</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Field label="Bank name"><Input value={d.bankName} onChange={(e) => setDon("bankName", e.target.value)} /></Field>
              <Field label="Branch"><Input value={d.bankBranch} onChange={(e) => setDon("bankBranch", e.target.value)} /></Field>
              <Field label="Account name"><Input value={d.bankAccountName} onChange={(e) => setDon("bankAccountName", e.target.value)} /></Field>
              <Field label="Account number"><Input value={d.bankAccountNumber} onChange={(e) => setDon("bankAccountNumber", e.target.value)} /></Field>
              <Field label="SWIFT"><Input value={d.swift} onChange={(e) => setDon("swift", e.target.value)} /></Field>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
              <Field label="MoMo provider"><Input value={d.momoProvider} onChange={(e) => setDon("momoProvider", e.target.value)} /></Field>
              <Field label="MoMo number"><Input value={d.momoNumber} onChange={(e) => setDon("momoNumber", e.target.value)} /></Field>
              <Field label="MoMo name"><Input value={d.momoName} onChange={(e) => setDon("momoName", e.target.value)} /></Field>
            </div>
            <Field label="Notes (optional)"><Textarea value={d.notes} onChange={(e) => setDon("notes", e.target.value)} rows={2} /></Field>
          </div>
        )}
      </Card>
    </AdminShell>
  );
}

/* ---------------- Pledges (per event) ---------------- */
export function AdminEventPledgesPage() {
  const { eventId } = useParams();
  const { event } = useEvent(eventId);
  const { leads, loading } = useLeads();
  const eventLeads = leads.filter((l) => l.eventId === eventId);
  const [busy, setBusy] = useState(null);

  const markReceived = async (lead) => {
    setBusy(lead.id);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const id = await saveDonation(null, {
        donorName: lead.donorName, donorEmail: lead.donorEmail, amount: Number(lead.intendedAmount),
        currency: lead.currency, method: lead.method === "either" ? "bank" : lead.method,
        status: "received", receivedDate: today, eventId, linkedLeadId: lead.id, notes: "Marked received from event pledge.",
      });
      await markDonationReceived(id, { receivedDate: today, amount: Number(lead.intendedAmount), eventId });
      await updateLeadStatus(lead.id, "fulfilled");
      showToast({ intent: "success", title: "Recorded as received" });
    } catch (err) {
      showToast({ intent: "danger", title: "Couldn't record", body: err.message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <AdminShell>
      <Link to="/admin/events" className="back-link"><Icon.ArrowLeft size={14} />Back to events</Link>
      <div className="row" style={{ marginTop: 16, marginBottom: 8, gap: 16, alignItems: "baseline" }}>
        <h1 className="t-3xl" style={{ margin: 0, flex: 1 }}>Pledges{event ? ` — ${event.title}` : ""}</h1>
        {event?.donation?.enabled && <Badge variant="received" noDot>{CURRENCY_FMT(event.totals?.receivedAmount || 0, event.donation?.currency || "GHS")} received</Badge>}
      </div>
      <p className="t-sm muted" style={{ marginTop: 0, marginBottom: 24 }}>People who pledged toward this event. Mark a pledge received once the transfer arrives.</p>

      {loading ? <Skeleton h={240} /> : eventLeads.length === 0 ? (
        <Card><EmptyState icon={<Icon.Users size={40} />} title="No pledges yet" body="Pledges submitted on this event's page will appear here." /></Card>
      ) : (
        <table className="table">
          <thead><tr><th>Name</th><th>Email</th><th style={{ width: 150 }}>Amount</th><th style={{ width: 120 }}>Status</th><th style={{ width: 130 }}>Submitted</th><th className="table__actions">Actions</th></tr></thead>
          <tbody>
            {eventLeads.map((l) => (
              <tr key={l.id}>
                <td style={{ fontWeight: 600 }}><Link to={`/admin/leads/${l.id}`}>{l.donorName}</Link></td>
                <td className="t-sm muted">{l.donorEmail}</td>
                <td>{CURRENCY_FMT(l.intendedAmount, l.currency)}</td>
                <td><Badge variant={l.status}>{l.status[0].toUpperCase() + l.status.slice(1)}</Badge></td>
                <td className="t-sm muted">{formatRelative(l.submittedAt)}</td>
                <td className="table__actions">
                  {l.status !== "fulfilled" ? (
                    <Button variant="secondary" size="sm" loading={busy === l.id} onClick={() => markReceived(l)}>Mark received</Button>
                  ) : <span className="t-sm muted">Received</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </AdminShell>
  );
}

export default AdminEventsListPage;
